import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Plus, Sparkles, Trash2, Wand2, Building2 } from 'lucide-react'
import { api, ApiError } from '../api'
import type { Avatar, Brand, Look, Asset, Template, ScriptDraft, JobDetail } from '../types'
import { go } from '../router'
import { AuthImage, WordCount } from '../components'
import { useGeneration, statusLabel, clearStoredTask } from '../tasks'
import {
  Button, Card, Field, Input, Textarea, Note, SectionLabel, StepHeader, Badge,
  ErrorText, Skeleton } from '../components/ui'
import { Select } from '../components/select'
import { cn } from '../lib/utils'

type Draft = {
  kind: 'generated' | 'insert'
  line: string
  gesture: string
  asset_id: string
	hook_text: string
}

const blank = (): Draft => ({ kind: 'generated', line: '', gesture: '', asset_id: '', hook_text: '' })

type WritingMode = 'choose' | 'generated' | 'manual'
type MovementChoice = 'auto' | 'stationary' | 'walking' | 'seated' | 'match_frame'

function scenesFromScript(script: ScriptDraft, demoAssetID: string): Draft[] {
  const written = script.scenes.map((scene, index) => ({
    kind: 'generated' as const,
    line: scene.line,
    gesture: '',
    asset_id: '',
		hook_text: index === 0 ? script.hook_text : '',
  }))
  if (!demoAssetID) return written
  return [
    ...written.slice(0, -1),
    { kind: 'insert', line: '', gesture: '', asset_id: demoAssetID, hook_text: '' } as Draft,
    ...written.slice(-1),
  ]
}

function blankManualScenes(openingCount: number, demoAssetID: string): Draft[] {
  const openings = Array.from({ length: openingCount }, blank)
  const cta = blank()
  return demoAssetID
    ? [...openings, { kind: 'insert', line: '', gesture: '', asset_id: demoAssetID, hook_text: '' }, cta]
    : [...openings, cta]
}

function hookUnits(text: string) {
  return Array.from(text).reduce((total, character) => {
    if (character === ' ') return total + 0.32
    if ("ilI1.,'!|".includes(character)) return total + 0.30
    if ('MWmw@%'.includes(character)) return total + 0.88
    return total + 0.58
  }, 0)
}

function splitHook(text: string) {
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (words.length < 2) return words
  let best = 1
  let bestWidth = Number.POSITIVE_INFINITY
  for (let index = 1; index < words.length; index += 1) {
    const width = Math.max(
      hookUnits(words.slice(0, index).join(' ')),
      hookUnits(words.slice(index).join(' ')),
    )
    if (width < bestWidth) {
      best = index
      bestWidth = width
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')]
}

function WrittenHookPreview({ ws, look, text, onClose }: {
  ws: string; look?: Look; text: string; onClose: () => void
}) {
  if (!look || !text.trim()) return null
  const lines = splitHook(text.slice(0, 80))
  const widest = Math.max(...lines.map(hookUnits), 1)
  // Same proportional bounds as production, scaled to this 360px preview.
  const fontSize = Math.max(13, Math.min(19, 295 / widest))
  return (
    <div className="w-full max-w-[360px] space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-ink-faint">Preview</p>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Close preview</Button>
      </div>
      <div className="relative aspect-[9/16] overflow-hidden rounded-[--radius-control] bg-surface-2">
        <AuthImage
          ws={ws} storageKey={look.start_frame_key} alt="Written hook preview"
          ratio="free" className="absolute inset-0 size-full rounded-none" thumbnail
        />
        <div className="absolute inset-x-[9%] top-[10.5%] flex flex-col items-center">
          {lines.map((line, index) => (
            <span
              key={`${line}-${index}`}
              className={cn(
                'relative inline-block whitespace-nowrap bg-black px-[0.55em] py-[0.28em]',
                'font-semibold leading-none text-white', index > 0 && '-mt-[0.18em]',
              )}
              style={{ fontFamily: 'Montserrat, sans-serif', fontSize }}
            >
              {line}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// Not a UUID, so it cannot collide with a saved template ID.
const AUTO = 'auto'

export function NewJob({ ws, sourceID }: { ws: string; sourceID?: string }) {
	// Every regeneration uses scene selection. Selecting every generated scene
	// replaces the old separate "full" action, while selecting fewer reuses the
	// untouched clips. Treat legacy /full links the same way.
	const partialRegeneration = !!sourceID
  // One brand per workspace, so there is nothing to pick -- it is resolved
  // and shown rather than chosen.
  const brand = useQuery({
    queryKey: ['brand', ws],
    queryFn: async () => {
      try {
        return await api.get<Brand>(`/api/workspaces/${ws}/brand`)
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
  })
  const avatars = useQuery({ queryKey: ['avatars', ws], queryFn: () => api.get<Avatar[]>(`/api/workspaces/${ws}/avatars`) })
  const templates = useQuery({
    queryKey: ['templates', ws],
    queryFn: () => api.get<Template[]>(`/api/workspaces/${ws}/templates`),
  })
  const source = useQuery({
    queryKey: ['job', ws, sourceID],
    queryFn: () => api.get<JobDetail>(`/api/workspaces/${ws}/jobs/${sourceID}`),
    enabled: !!sourceID,
  })

  const brandID = brand.data?.id ?? ''
  const [avatarID, setAvatarID] = useState('')
  const [lookID, setLookID] = useState('')
  const [movementMode, setMovementMode] = useState<MovementChoice>('auto')
  const [painPoint, setPainPoint] = useState('')
  const [scenes, setScenes] = useState<Draft[]>([blank()])
  const [writingMode, setWritingMode] = useState<WritingMode>('choose')
  // Auto lets the writer choose the structure after reading the complete
  // brief and demo. A saved pattern is only imposed when the user deliberately
  // selects it; choosing one randomly here made scripts bend around unsuitable
  // devices such as numbered teases with no real list to deliver.
  const [hookID, setHookID] = useState(AUTO)
  const [ctaID, setCtaID] = useState(AUTO)
  const [openingCount, setOpeningCount] = useState(2)
  const [hookPreviewOpen, setHookPreviewOpen] = useState(false)
  // Chosen before writing, because the writer needs to know
  // what it is leading into: without it the openings can promise something the
  // footage never shows and the CTA lands as if the demo had not happened.
  const [demoAssetID, setDemoAssetID] = useState('')
	const [regenerateScenes, setRegenerateScenes] = useState<number[]>([])
  const hydratedSource = useRef<string | null>(null)

  // A regeneration is a new composition seeded from the old video's saved
  // inputs. Hydrate once so polling/refetching cannot overwrite edits made on
  // this page, and never copy run state, costs, takes or output identifiers.
  useEffect(() => {
    if (!sourceID || !source.data || hydratedSource.current === sourceID) return
    const original = source.data
    setAvatarID(original.job.avatar_id)
    setLookID(original.job.look_id ?? '')
    setMovementMode(original.job.movement_mode || 'match_frame')
    setPainPoint(original.job.pain_point ?? '')
    setScenes(original.scenes.map(scene => ({
      kind: scene.kind,
      line: scene.line ?? '',
      gesture: scene.gesture ?? '',
      asset_id: scene.asset_id ?? '',
		hook_text: scene.hook_text ?? '',
    })))
    const demo = original.scenes.find(scene => scene.kind === 'insert')
    setDemoAssetID(demo?.asset_id ?? '')
    setOpeningCount(Math.max(1, original.scenes.filter(scene => scene.kind === 'generated').length - 1))
    hydratedSource.current = sourceID
  }, [sourceID, source.data])

  const looks = useQuery({
    queryKey: ['looks', ws, avatarID],
    queryFn: () => api.get<Look[]>(`/api/workspaces/${ws}/avatars/${avatarID}/looks`),
    enabled: !!avatarID,
  })
  const assets = useQuery({
    queryKey: ['assets', ws, brandID],
    queryFn: () => api.get<Asset[]>(`/api/workspaces/${ws}/brands/${brandID}/assets?kind=insert`),
    enabled: !!brandID,
  })

  const draft = useGeneration<ScriptDraft>(ws, 'scripts/draft')
  const [editedDraft, setEditedDraft] = useState<ScriptDraft | null>(null)

  // Task results are immutable receipts. Keep edits in local form state so a
  // person can refine the copy without rewriting the stored generation result
  // or losing changes to a query refresh.
  useEffect(() => {
    if (!draft.result) return
    setWritingMode('generated')
    setEditedDraft({
      ...draft.result,
      scenes: draft.result.scenes.map(scene => ({ ...scene })),
    })
  }, [draft.task?.id, draft.result])

  // A draft belongs to the ad being composed, and is forgotten on the way out.
  //
  // The task id is persisted so a RELOAD lands back on work in progress. That
  // is worth keeping, but it also meant the next ad opened showing the last
  // ad's lines as if they belonged to the new video. Leaving the
  // page ends the composition; a reload does not run this cleanup, so the
  // resume it was written for still works.
  useEffect(() => () => clearStoredTask(ws, 'scripts/draft'), [ws])

  const currentDraft = editedDraft ?? draft.result
  const videoScenes = !partialRegeneration && writingMode === 'generated' && currentDraft
    ? scenesFromScript(currentDraft, demoAssetID)
    : scenes

  const create = useMutation({
    mutationFn: () =>
      api.post<{ job: { id: string } }>(`/api/workspaces/${ws}/jobs`, {
        brand_id: brandID,
        avatar_id: avatarID,
        look_id: lookID || null,
        movement_mode: movementMode,
        pain_point: painPoint || null,
		source_job_id: sourceID || null,
		regenerate_scenes: partialRegeneration ? regenerateScenes : [],
        start: true,
        scenes: videoScenes.map(s =>
          s.kind === 'insert'
            ? { kind: 'insert', asset_id: s.asset_id }
            : {
                kind: 'generated',
                line: s.line,
                gesture: s.gesture || null,
				hook_text: s.hook_text || null,
				chain_from: null,
              },
        ),
      }),
    onSuccess: res => {
      // Saved: the draft has been consumed and must not follow you to the
      // next ad.
      clearStoredTask(ws, 'scripts/draft')
      go({ name: 'job', id: res.job.id })
    },
  })

  const set = (i: number, patch: Partial<Draft>) =>
    setScenes(s => s.map((x, j) => (j === i ? { ...x, ...patch } : x)))

	const toggleRegeneration = (position: number, checked: boolean) => {
	  setRegenerateScenes(current => {
		const next = new Set(current)
		if (checked) next.add(position)
		else next.delete(position)
		return [...next].sort((a, b) => a - b)
	  })
	}
  const looksList = looks.data ?? []
  const selectedLook = looksList.find(look => look.id === lookID)
  const canChooseMovement = selectedLook?.posture === 'standing' &&
    selectedLook.space === 'outdoor' && selectedLook.camera_style === 'handheld'
  const displayedMovement: MovementChoice = selectedLook?.posture === 'seated'
    ? 'seated'
    : selectedLook?.posture === 'match_frame'
      ? 'match_frame'
      : canChooseMovement ? movementMode : 'stationary'
  const demos = assets.data ?? []
  const hooks = templates.data?.filter(t => t.kind === 'hook') ?? []
  const ctas = templates.data?.filter(t => t.kind === 'cta') ?? []

  /** Auto supplies no template constraint. The writing prompt still provides
   *  structural guidance; explicit saved patterns remain user overrides. */
  const resolve = (value: string, list: Template[]): Template | undefined => {
    if (value === AUTO) return undefined
    return list.find(t => t.id === value)
  }

  const ready =
    brandID && avatarID && lookID &&
		videoScenes.length > 0 &&
		videoScenes.every(s => (s.kind === 'insert' ? s.asset_id : s.line.trim())) &&
		!(writingMode === 'generated' && draft.busy) &&
		(!partialRegeneration || regenerateScenes.length > 0)
	const generatedPositions = scenes.flatMap((scene, index) =>
	  scene.kind === 'generated' ? [index + 1] : [])
	const allGeneratedSelected = generatedPositions.length > 0 &&
	  generatedPositions.every(position => regenerateScenes.includes(position))

  return (
    // Bottom padding clears the sticky save bar, which would otherwise sit
    // permanently over the last scene rather than letting it scroll clear.
    <div className="space-y-6 pb-24">
      <Button variant="ghost" size="sm" className="-ml-3" onClick={() => go({ name: 'jobs' })}>
        <ArrowLeft /> Videos
      </Button>

      <div>
		<h1 className="text-2xl">{partialRegeneration ? 'Regenerate' : 'New video'}</h1>
		<p className="mt-1 text-sm text-ink-faint">
		  {partialRegeneration
			? 'Choose any scenes to replace, or select all of them. Unselected clips are reused in a new comparison video.'
			: 'Two steps: choose the cast and product, then generate or write the script. Create video starts generation and opens its progress page.'}
        </p>
      </div>

      {sourceID && source.isLoading && <Skeleton className="h-24 w-full" />}
      {sourceID && <ErrorText error={source.error} />}

      {/* --- the setup ------------------------------------------------------ */}
      <Card className="space-y-4">
        <StepHeader
          n={1} title="Cast and product"
          hint="Who is on camera, where they are filmed, and what the video is about."
        />

        {brand.data ? (
          <div className="flex items-center gap-3 rounded-[--radius-control] bg-surface-2/50 px-4 py-3">
            <Building2 className="size-4 shrink-0 text-ink-faint" />
            <div className="min-w-0">
              <div className="text-sm font-medium">{brand.data.name}</div>
              <div className="truncate text-xs text-ink-faint">
                spoken as “{brand.data.spoken_name}”
              </div>
            </div>
          </div>
        ) : (
          <Note tone="warn">
            This workspace has no brand yet. Set one up under Brand Context — without it a
            draft would be guesswork.
          </Note>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Avatar">
            <Select
              value={avatarID}
              onValueChange={v => { setAvatarID(v); setLookID(''); setMovementMode('auto') }}
			  disabled={partialRegeneration}
              placeholder="Choose…"
              items={(avatars.data ?? []).map(a => ({
                value: a.id,
                label: a.name,
                thumbnail: (
                  <AuthImage
                    ws={ws} storageKey={a.closeup_key || a.source_key} alt=""
                    ratio="free" className="size-7 shrink-0" thumbnail
                  />
                ),
                thumbnailPreview: (
                  <AuthImage
                    ws={ws} storageKey={a.closeup_key || a.source_key} alt={a.name}
                    ratio="portrait" className="w-32" thumbnail
                  />
                ),
              }))}
            />
          </Field>
          <Field label="Look">
            <Select
              value={lookID} onValueChange={v => { setLookID(v); setMovementMode('auto') }}
			  disabled={partialRegeneration}
              placeholder={avatarID ? 'Choose…' : 'Pick an avatar first'}
              items={looksList.map(l => ({
                value: l.id,
                label: l.name || l.id.slice(0, 8),
                hint: `${l.mic} · ${l.space}`,
                thumbnail: (
                  <AuthImage
                    ws={ws} storageKey={l.start_frame_key} alt=""
                    ratio="free" className="size-7 shrink-0" thumbnail
                  />
                ),
                thumbnailPreview: (
                  <AuthImage
                    ws={ws} storageKey={l.start_frame_key} alt={l.name || 'Look preview'}
                    ratio="portrait" className="w-32" thumbnail
                  />
                ),
              }))}
            />
          </Field>
        </div>

        {selectedLook && (
          <Field
            label="On-camera movement"
            help={canChooseMovement
              ? 'Auto chooses once for the whole video, so every actor scene either walks or stays in place.'
              : selectedLook.posture === 'seated'
                ? 'This look establishes a seated setup.'
                : selectedLook.posture === 'match_frame'
                  ? 'Movement follows the uploaded reference.'
                  : 'This fixed-camera look stays in place.'}
          >
            <Select
              value={displayedMovement}
              onValueChange={value => setMovementMode(value as MovementChoice)}
              disabled={!canChooseMovement || partialRegeneration}
              items={canChooseMovement ? [
                ...(partialRegeneration && displayedMovement === 'match_frame'
                  ? [{ value: 'match_frame', label: 'Match source video' }]
                  : []),
                { value: 'auto', label: 'Auto' },
                { value: 'stationary', label: 'Stay in place' },
                { value: 'walking', label: 'Walk' },
              ] : [
                { value: displayedMovement, label: displayedMovement === 'seated'
                  ? 'Seated'
                  : displayedMovement === 'match_frame' ? 'Match look' : 'Stay in place' },
              ]}
            />
          </Field>
        )}

        {avatarID && looksList.length === 0 && !looks.isLoading && (
          <Note tone="warn">
            This avatar has no looks yet. A look supplies the start frame, mic, space and
            ambience — add one from Avatars in the sidebar.
          </Note>
        )}

        <Field
          label="What should this video cover?"
          help="Example: How a 3-person marketing team can plan, approve, and publish 20 client posts a week without jumping between tools."
        >
          <Textarea
            rows={3}
            value={painPoint} onChange={e => setPainPoint(e.target.value)}
            placeholder="Describe the topic, problem or message you want the video to focus on."
          />
        </Field>
      </Card>

	  {/* --- drafting -------------------------------------------------------- */}
	  {!partialRegeneration && <Card className="space-y-4 border-brand/25 bg-brand/[0.04]">
        <div className="flex items-baseline gap-2.5">
          <span className="grid size-5 shrink-0 place-items-center rounded-full bg-brand/15 text-[11px] font-medium text-brand">
            2
          </span>
          <div>
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Wand2 className="size-3.5 text-brand" /> Choose how to write the script
            </span>
            <p className="mt-0.5 text-xs leading-relaxed text-ink-faint">
              Generate a draft from the brand context and your saved patterns, or open the
              scene editor and write every line yourself.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Hook pattern">
            <Select
              value={hookID} onValueChange={setHookID} placeholder="Auto"
              items={[
                { value: AUTO, label: 'Auto', hint: 'Chooses the best opening from the brief and demo.' },
                ...hooks.map(t => ({ value: t.id, label: t.name }))]}
            />
          </Field>
          <Field label="CTA pattern">
            <Select
              value={ctaID} onValueChange={setCtaID} placeholder="Auto"
              items={[
                { value: AUTO, label: 'Auto', hint: 'Chooses a natural next action from the complete script.' },
                ...ctas.map(t => ({ value: t.id, label: t.name }))]}
            />
          </Field>
          <Field
            label="Product demo"
            help={demoAssetID ? 'The lines will lead into it and pick up after it.' : undefined}
          >
            <Select
              value={demoAssetID} onValueChange={setDemoAssetID} placeholder="None"
              items={[
                { value: '', label: 'None' },
                ...demos.map(a => ({
                  value: a.id,
                  label: a.name,
                  hint: a.transcript ? undefined : 'no transcript yet',
                  thumbnail: (
                    <AuthImage
                      ws={ws} storageKey={a.poster_key} alt=""
                      ratio="free" className="size-7 shrink-0"
                    />
                  ),
                  thumbnailPreview: (
                    <AuthImage
                      ws={ws} storageKey={a.poster_key} alt={a.name}
                      ratio="portrait" className="w-32"
                    />
                  ),
                })),
              ]}
            />
          </Field>
          <Field label="Length">
            <Select
              value={String(openingCount)} onValueChange={v => setOpeningCount(Number(v))}
              items={[
                // Names the demo when one is chosen, because the draft places
                // it: the label describes the ad you actually get.
                { value: '1', label: demoAssetID ? '1 opening + demo + CTA' : '1 opening + CTA' },
                { value: '2', label: demoAssetID ? '2 openings + demo + CTA' : '2 openings + CTA' },
                { value: '3', label: demoAssetID ? '3 openings + demo + CTA' : '3 openings + CTA' },
              ]}
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            disabled={!brandID || draft.busy}
            onClick={() => {
              setWritingMode('generated')
              const hook = resolve(hookID, hooks)
              const cta = resolve(ctaID, ctas)
              draft.submit.mutate({
                brand_id: brandID, avatar_id: avatarID, look_id: lookID,
                pain_point: painPoint, scene_count: openingCount, duration_s: 10,
                hook_template_id: hook?.id ?? '', cta_template_id: cta?.id ?? '',
                demo_asset_id: demoAssetID,
              })
            }}
          >
            <Sparkles /> {draft.busy
              ? statusLabel(draft.task) || 'Submitting…'
              : draft.result ? 'Generate another script' : 'Generate script'}
          </Button>
          <Button variant="secondary" onClick={() => {
            setWritingMode('manual')
            setScenes(blankManualScenes(openingCount, demoAssetID))
          }}>
            I'll write it myself
          </Button>
          {!brandID && <span className="text-sm text-ink-faint">Set up the brand first.</span>}
        </div>

        {(draft.busy || draft.result) && (hookID === AUTO || ctaID === AUTO) && (
          <p className="text-xs text-ink-faint">
            {hookID === AUTO && (
              <>Hook: <span className="text-ink-dim">matched to the complete script</span></>
            )}
            {hookID === AUTO && ctaID === AUTO && ' · '}
            {ctaID === AUTO && (
              <>CTA: <span className="text-ink-dim">
                {demoAssetID ? 'matched to demo' : 'matched to the complete script'}
              </span></>
            )}
          </p>
        )}

        <ErrorText error={draft.error} />
        {draft.failed && <ErrorText error={draft.failed} />}

        {/* This is the only generated-script editor. These exact lines are
            submitted when Create video is clicked; there is no second review
            screen containing the same fields. */}
        {writingMode === 'generated' && currentDraft && (
          <div className="space-y-3 rounded-[--radius-control] border border-line bg-surface-1 p-4">
            <SectionLabel>Script</SectionLabel>
            <ol className="space-y-2.5">
              {currentDraft.scenes.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="grid size-5 shrink-0 place-items-center rounded-full bg-surface-3 text-[11px] text-ink-dim">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Textarea
                      rows={2}
                      value={s.line}
                      aria-label={`Scene ${i + 1} script`}
                      onChange={event => setEditedDraft(current => current ? {
                        ...current,
                        scenes: current.scenes.map((scene, index) =>
                          index === i ? { ...scene, line: event.target.value } : scene),
                      } : current)}
                    />
                    <p className="mt-0.5 text-xs text-ink-faint">
                      <WordCount text={s.line} />
                    </p>
                  </div>
                </li>
              ))}
            </ol>
			<div className="space-y-2">
			  <Field label="Written hook">
				<Input
				  value={currentDraft.hook_text ?? ''}
				  placeholder="A short hook viewers understand on mute"
				  maxLength={80}
                  onChange={event => {
                    const value = event.target.value
                    if (!value.trim()) setHookPreviewOpen(false)
                    setEditedDraft(current => current ? {
                      ...current, hook_text: value,
                    } : current)
                  }}
				/>
			  </Field>
			  {!hookPreviewOpen ? (
				<Button
				  type="button" variant="secondary" size="sm"
				  disabled={!selectedLook || !currentDraft.hook_text?.trim()}
				  title={!selectedLook ? 'Select a look to preview the hook' : !currentDraft.hook_text?.trim() ? 'Enter a written hook first' : undefined}
				  onClick={() => setHookPreviewOpen(true)}
				>
				  Preview hook
				</Button>
			  ) : (
				<WrittenHookPreview
				  ws={ws} look={selectedLook} text={currentDraft.hook_text ?? ''}
				  onClose={() => setHookPreviewOpen(false)}
				/>
			  )}
			</div>

            {currentDraft.notes && (
              <Note>{currentDraft.notes}</Note>
            )}
          </div>
        )}
	  </Card>}

      {/* Manual writing and regeneration still need scene-level controls, but
          they belong to step 2 rather than repeating an approved AI script in
          a third step. */}
      {(partialRegeneration || writingMode === 'manual') && <section className="space-y-3">
        <div className="flex items-start justify-between gap-3">
		  {partialRegeneration ? (
            <StepHeader
              n={2} title="Choose scenes"
              hint="Select the generated clips to replace. Product footage is reused."
            />
		  ) : (
            <div>
              <SectionLabel>Write the scenes</SectionLabel>
              <p className="mt-1 text-xs text-ink-faint">Write each spoken clip in order. Product footage is already placed from your selection above.</p>
            </div>
		  )}
		  {partialRegeneration ? (
			<Button
			  variant="ghost" size="sm" className="shrink-0"
			  onClick={() => setRegenerateScenes(allGeneratedSelected ? [] : generatedPositions)}
			>
			  {allGeneratedSelected ? 'Clear selection' : 'Select all scenes'}
			</Button>
		  ) : (
			<Button
			  variant="ghost" size="sm" className="shrink-0"
			  onClick={() => setScenes(s => [...s, blank()])}
			>
			  <Plus /> Add scene
			</Button>
		  )}
        </div>

        <div className="space-y-3">
          {scenes.map((s, i) => (
            <Card key={i} className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
				{partialRegeneration && s.kind === 'generated' && (
				  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
					<input
					  type="checkbox"
					  checked={regenerateScenes.includes(i + 1)}
					  onChange={event => toggleRegeneration(i + 1, event.target.checked)}
					  className="size-3.5 accent-[var(--color-brand)]"
					/>
					Regenerate
				  </label>
				)}
                <span className="grid size-7 place-items-center rounded-full bg-surface-2 text-xs font-medium text-ink-dim">
                  {i + 1}
                </span>
                <Select
                  size="sm" className="w-52"
                  value={s.kind}
                  onValueChange={v => set(i, { kind: v as Draft['kind'] })}
				  disabled={partialRegeneration}
                  items={[
                    { value: 'generated', label: 'Generated' },
                    // Same name as on the Assets page: two words for one thing
                    // is how someone ends up looking for a section that does
                    // not exist.
                    { value: 'insert', label: 'Product demo', hint: 'recorded footage' },
                  ]}
                />
                <div className="flex-1" />
				{scenes.length > 1 && !partialRegeneration && (
                  <Button
                    variant="ghost" size="icon" title="Remove scene"
                    onClick={() => setScenes(x => x.filter((_, j) => j !== i))}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>

              {s.kind === 'insert' ? (
                <Field label="Footage">
                  <Select
                    value={s.asset_id} onValueChange={v => set(i, { asset_id: v })}
					disabled={partialRegeneration}
                    placeholder="Choose footage…"
                    items={(assets.data ?? []).map(a => ({
                      value: a.id,
                      label: a.name,
                      hint: a.duration_s ? `${a.duration_s.toFixed(1)}s` : undefined,
                    }))}
                  />
                </Field>
              ) : (
                <>
                  <Textarea
                    rows={3} value={s.line}
                    onChange={e => set(i, { line: e.target.value })}
					disabled={partialRegeneration && !regenerateScenes.includes(i + 1)}
                    placeholder="The exact words to be spoken."
                  />
                  <WordCount text={s.line} />
                </>
              )}
            </Card>
          ))}
		  {!partialRegeneration && (
			<div className="space-y-2 rounded-[--radius-control] border border-line bg-surface-1 p-4">
			  <Field label="Written hook">
				<Input
				  value={scenes.find(scene => scene.kind === 'generated')?.hook_text ?? ''}
				  placeholder="A short hook viewers understand on mute"
				  maxLength={80}
				  onChange={event => {
					const value = event.target.value
					if (!value.trim()) setHookPreviewOpen(false)
					setScenes(current => {
					  const first = current.findIndex(scene => scene.kind === 'generated')
					  return current.map((scene, index) => index === first
						? { ...scene, hook_text: value }
						: scene)
					})
				  }}
				/>
			  </Field>
			  {!hookPreviewOpen ? (
				<Button
				  type="button" variant="secondary" size="sm"
				  disabled={!selectedLook || !scenes.find(scene => scene.kind === 'generated')?.hook_text.trim()}
				  title={!selectedLook ? 'Select a look to preview the hook' : undefined}
				  onClick={() => setHookPreviewOpen(true)}
				>
				  Preview hook
				</Button>
			  ) : (
				<WrittenHookPreview
				  ws={ws} look={selectedLook}
				  text={scenes.find(scene => scene.kind === 'generated')?.hook_text ?? ''}
				  onClose={() => setHookPreviewOpen(false)}
				/>
			  )}
			</div>
		  )}
        </div>
      </section>}

      {(partialRegeneration || writingMode !== 'choose') && <ErrorText error={create.error} />}

      {(partialRegeneration || writingMode !== 'choose') && <div className={cn('sticky bottom-4 flex items-center gap-3 rounded-[--radius-card]',
        'border border-line bg-surface-1/90 p-3 backdrop-blur-xl')}>
        <Button variant="primary" onClick={() => create.mutate()} disabled={!ready || create.isPending}>
          {/* Not "Save draft": step 2 already calls its output a draft, and
              two meanings of the word on one page is most of why the order
              here was hard to read. */}
		  {create.isPending ? 'Starting…' : partialRegeneration ? 'Create comparison video' : 'Create video'}
        </Button>
        <div className="flex-1" />
		{partialRegeneration
		  ? <Badge>{regenerateScenes.length} regenerating · {scenes.length - regenerateScenes.length} reused</Badge>
		  : <Badge>{videoScenes.length} scene{videoScenes.length === 1 ? '' : 's'}</Badge>}
      </div>}
    </div>
  )
}
